import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<button type=button class=flight__settings-toggle data-testid=preferences-button>`);
import { browserSettingsStore } from "../audio/settings.ts";
import { saveRealism } from "../mission/realism-settings.ts";
import { FlightPreferences } from "./PreferencesOverlay.tsx";

/**
 * The flight view's settings chrome: **one button**, and the PREFERENCES panel
 * it opens (`O` opens the same panel from the keyboard — `keys.ts`).
 *
 * ## What this replaced, and why
 *
 * Until this wave the corner held a flex row of eight chips — REALISTIC
 * INSTRUMENTS, PROPELLER EFFECT, KILL CAMERA, ENHANCED TERRAIN, SKY, ENHANCED
 * EFFECTS, ENHANCED MODELS, STICK — plus an AUDIO button opening a slider
 * popover, and the Realism Panel was reachable from neither. The row was at
 * capacity: `app.css` told the next lane in as many words that a ninth chip had
 * to *replace* one rather than be appended, every chip needed its own entry in
 * `keys.ts`'s overlay-exemption list (and a missing entry meant a click on it
 * **fired the guns**), and a flat row of shouting labels could not say which
 * setting belonged with which.
 *
 * All of it now lives in one place — `shell/model/preferences-screen.ts`, drawn
 * in the original's own panel vocabulary, in three named sections with the
 * Realism Panel one button away. **The chip row is gone rather than kept as a
 * quick-access strip**: two ways to change one setting is two things to keep in
 * step and two hazards in the exemption list, and the panel is one keypress
 * away from the same corner. What the row was actually good for — seeing the
 * change immediately — is kept by the panel applying every change live through
 * the same `live-settings.ts` path the chips used, so a sky or a propeller is
 * already different when it closes.
 *
 * The button says which key opens the panel, and the panel says it too. It also
 * still reports a muted mixer, which is the one setting worth seeing without
 * opening anything.
 */
export function FlightSettingsControls(props) {
  /**
   * Each group goes back to the route that owns it, which is what makes a
   * change live: these are `live-settings.ts`'s functions, so they write
   * storage *and* the running scene. The Realism Panel has no route-side owner
   * — it is read from storage per sortie (`routes/Fly.tsx`) — so it is
   * persisted here.
   */
  const sinks = {
    audio: next => props.onChangeAudio(next),
    cockpit: next => props.onChangeCockpit(next),
    effects: next => props.onChangeEffects(next),
    stick: next => props.onChangeStick(next),
    bail: next => props.onChangeBail(next),
    realism: next => {
      const store = browserSettingsStore();
      if (store) saveRealism(store, next);
    }
  };
  return [(() => {
    var _el$ = _tmpl$();
    _el$.$$click = () => props.onToggleSettingsVisible();
    _$insert(_el$, () => props.audioSettings.muted ? "O · PREFERENCES (MUTED)" : "O · PREFERENCES");
    _$effect(() => _$setAttribute(_el$, "aria-expanded", props.settingsVisible));
    return _el$;
  })(), _$createComponent(FlightPreferences, {
    get open() {
      return props.settingsVisible;
    },
    sinks: sinks,
    get onClose() {
      return props.onDismissSettings;
    },
    get gamepad() {
      return props.gamepad;
    }
  })];
}
_$delegateEvents(["click"]);